import { useCallback, useEffect } from 'react';
import { useTheme } from './hooks/useTheme';
import { useNotifications } from './hooks/useNotifications';
import { useSSE } from './hooks/useSSE';
import { useChat } from './hooks/useChat';
import { usePersistedState } from './hooks/usePersistedState';
import { Notifications } from './components/Notifications';
import { ThemeToggle } from './components/ThemeToggle';
import { ConnectionSetup } from './components/ConnectionSetup';
import { Chat } from './components/Chat';
import type { ConnectionConfig, BotResponse } from './types/telegram';

export default function App(): React.JSX.Element {
  const { theme, setTheme } = useTheme();
  const { notifications, addNotification, removeNotification } = useNotifications();
  const [config, setConfig, clearConfig] = usePersistedState<ConnectionConfig | null>(
    'connectionConfig',
    null
  );

  const handleError = useCallback(
    (message: string) => {
      addNotification('error', message);
    },
    [addNotification]
  );

  const {
    messages,
    pendingCallbackAlert,
    sendMessage,
    sendCallbackQuery,
    handleBotResponse,
    clearMessages,
    dismissCallbackAlert,
  } = useChat({ config, onError: handleError });

  const handleSSEMessage = useCallback(
    (response: BotResponse) => {
      handleBotResponse(response);
    },
    [handleBotResponse]
  );

  const handleSSEStatusChange = useCallback(
    (status: string) => {
      if (status === 'connected') {
        addNotification('success', 'Connected to bot');
      } else if (status === 'error') {
        addNotification('error', 'Connection lost, reconnecting...');
      }
    },
    [addNotification]
  );

  const { status, connect, disconnect } = useSSE({
    onMessage: handleSSEMessage,
    onError: (error) => handleError(error.message),
    onStatusChange: handleSSEStatusChange,
  });

  const handleConnect = useCallback(
    (newConfig: ConnectionConfig) => {
      setConfig(newConfig);
      const sseUrlWithSession = `${newConfig.sseUrl}/${newConfig.sessionId}`;
      connect(sseUrlWithSession);
    },
    [setConfig, connect]
  );

  const handleDisconnect = useCallback(() => {
    disconnect();
    clearConfig();
    addNotification('info', 'Disconnected from bot');
  }, [disconnect, clearConfig, addNotification]);

  // Reconnect on page load if config exists
  useEffect(() => {
    if (config && status === 'disconnected') {
      const sseUrlWithSession = `${config.sseUrl}/${config.sessionId}`;
      connect(sseUrlWithSession);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  const handleSend = useCallback(
    (text: string) => {
      void sendMessage(text);
    },
    [sendMessage]
  );

  const handleCallback = useCallback(
    (data: string, message: Parameters<typeof sendCallbackQuery>[1]) => {
      void sendCallbackQuery(data, message);
    },
    [sendCallbackQuery]
  );

  const isConnected = status === 'connected';

  return (
    <div className="flex h-screen flex-col bg-telegram-bg dark:bg-telegram-bg-dark">
      <Notifications notifications={notifications} onDismiss={removeNotification} />

      {/* Top Bar */}
      <header className="flex items-center justify-between border-b border-gray-200 bg-white px-4 py-2 dark:border-gray-700 dark:bg-telegram-bg-dark">
        <h1 className="text-lg font-semibold">{'\uD83D\uDD27'} Telegram Bot Tester</h1>
        <ThemeToggle theme={theme} onChange={setTheme} />
      </header>

      {/* Main Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <aside className="w-80 flex-shrink-0 overflow-y-auto border-r border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-telegram-bg-dark">
          <ConnectionSetup
            onConnect={handleConnect}
            onDisconnect={handleDisconnect}
            status={status}
          />

          <div className="mt-4 rounded-lg bg-telegram-secondary-bg p-4 dark:bg-telegram-secondary-bg-dark">
            <h3 className="mb-2 font-medium">{'\uD83D\uDCD6'} How to use</h3>
            <ol className="list-inside list-decimal space-y-1 text-sm text-telegram-text-secondary dark:text-telegram-text-secondary-dark">
              <li>Enter your bot webhook URL</li>
              <li>Enter the SSE debug endpoint</li>
              <li>Click Connect</li>
              <li>Send messages to test your bot</li>
            </ol>
          </div>
        </aside>

        {/* Chat Area */}
        <main className="relative flex-1">
          <Chat
            messages={messages}
            onSend={handleSend}
            onCallback={handleCallback}
            onClear={clearMessages}
            disabled={!isConnected}
            pendingAlert={pendingCallbackAlert}
            onDismissAlert={dismissCallbackAlert}
          />
        </main>
      </div>
    </div>
  );
}
